import { createEffect, createSignal, onCleanup } from 'solid-js';

import type { COUNTRY_CODE } from '~/lib/graphql/types';

const POSTAL_CODE_6_DIGIT = /\d{6}/;
const DEBOUNCE_MS = 300;

export interface PlaceSuggestion {
    placeId: string;
    text: string;
    prediction: google.maps.places.PlacePrediction;
}

export interface SelectResult {
    lat: number;
    lng: number;
    address: string;
    addressComponents: google.maps.GeocoderAddressComponent[];
}

function isPostalCodeSuggestion(suggestion: PlaceSuggestion): boolean {
    const prediction =
        suggestion.prediction as google.maps.places.PlacePrediction & {
            types?: string[];
        };
    if (prediction.types?.includes('postal_code')) return true;
    return POSTAL_CODE_6_DIGIT.test(suggestion.text);
}

function toV1AddressComponents(
    v2Components: google.maps.places.AddressComponent[]
): google.maps.GeocoderAddressComponent[] {
    return v2Components.map((c) => ({
        long_name: c.longText || '',
        short_name: c.shortText || '',
        types: c.types
    }));
}

export function usePlacesAutocomplete(countryCode: () => COUNTRY_CODE) {
    const [suggestions, setSuggestions] = createSignal<PlaceSuggestion[]>([]);
    const [enrichedLabels, setEnrichedLabels] = createSignal<
        Record<string, string>
    >({});
    const [inputValue, setInputValue] = createSignal('');

    let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
    let geocoder: google.maps.Geocoder | null = null;
    const enrichCache = new Map<string, string>();
    let debounceTimer: number | null = null;
    let currentSuggestions: PlaceSuggestion[] = [];

    function getSessionToken() {
        if (!sessionToken) {
            sessionToken = new google.maps.places.AutocompleteSessionToken();
        }
        return sessionToken;
    }

    function resetSessionToken() {
        sessionToken = null;
    }

    async function enrichSuggestions(suggestionsToEnrich: PlaceSuggestion[]) {
        const postalCodeSuggestions = suggestionsToEnrich.filter(
            (s) => isPostalCodeSuggestion(s) && !enrichCache.has(s.placeId)
        );

        if (!postalCodeSuggestions.length) return;

        if (!geocoder) {
            geocoder = new google.maps.Geocoder();
        }

        await Promise.all(
            postalCodeSuggestions.map(async (suggestion) => {
                try {
                    const response = await geocoder!.geocode({
                        placeId: suggestion.placeId
                    });
                    const location = response?.results?.[0]?.geometry?.location;
                    if (!location) return;

                    const reverseResponse = await geocoder!.geocode({
                        location: { lat: location.lat(), lng: location.lng() }
                    });

                    const formattedAddress =
                        reverseResponse?.results?.[0]?.formatted_address;
                    if (formattedAddress) {
                        enrichCache.set(suggestion.placeId, formattedAddress);

                        if (
                            currentSuggestions.some(
                                (s) => s.placeId === suggestion.placeId
                            )
                        ) {
                            setEnrichedLabels((prev) => ({
                                ...prev,
                                [suggestion.placeId]: formattedAddress
                            }));
                        }
                    }
                } catch (error) {
                    console.warn(
                        '[PlacesAutocomplete] Failed to enrich suggestion:',
                        error
                    );
                }
            })
        );
    }

    async function fetchSuggestions(input: string) {
        if (!input.trim()) {
            setSuggestions([]);
            currentSuggestions = [];
            return;
        }

        try {
            const { suggestions: results } =
                await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    {
                        input,
                        includedRegionCodes: [countryCode().toLowerCase()],
                        sessionToken: getSessionToken()
                    }
                );

            const mapped: PlaceSuggestion[] = results
                .filter((r) => r.placePrediction)
                .map((r) => ({
                    placeId: r.placePrediction!.placeId,
                    text: r.placePrediction!.text.text,
                    prediction: r.placePrediction!
                }));

            setSuggestions(mapped);
            currentSuggestions = mapped;

            // Apply cached enrichments
            const cachedEnrichments = Object.fromEntries(
                mapped
                    .map(
                        (s) => [s.placeId, enrichCache.get(s.placeId)] as const
                    )
                    .filter(([, v]) => v != null)
            );
            if (Object.keys(cachedEnrichments).length) {
                setEnrichedLabels((prev) => ({
                    ...prev,
                    ...cachedEnrichments
                }));
            }

            enrichSuggestions(mapped);
        } catch (error) {
            console.warn(
                '[PlacesAutocomplete] Failed to fetch suggestions:',
                error
            );
            setSuggestions([]);
            currentSuggestions = [];
        }
    }

    // Debounced fetch on input change
    createEffect(() => {
        const value = inputValue();
        if (debounceTimer) clearTimeout(debounceTimer);

        debounceTimer = window.setTimeout(() => {
            fetchSuggestions(value);
        }, DEBOUNCE_MS);
    });

    onCleanup(() => {
        if (debounceTimer) clearTimeout(debounceTimer);
    });

    async function onSelect(index: number): Promise<SelectResult | null> {
        const suggestion = currentSuggestions[index];
        if (!suggestion) return null;

        try {
            const place = suggestion.prediction.toPlace();
            if (!place) return null;

            await place.fetchFields({
                fields: ['formattedAddress', 'addressComponents', 'location']
            });

            resetSessionToken();

            const address =
                enrichCache.get(suggestion.placeId) ||
                place.formattedAddress ||
                suggestion.text;

            const addressComponents = place.addressComponents
                ? toV1AddressComponents(place.addressComponents)
                : [];

            return {
                lat: place.location?.lat() ?? 0,
                lng: place.location?.lng() ?? 0,
                address,
                addressComponents
            };
        } catch (error) {
            console.warn('[PlacesAutocomplete] Failed to select place:', error);
            return null;
        }
    }

    function clearSuggestions() {
        setSuggestions([]);
        currentSuggestions = [];
    }

    function clear() {
        setInputValue('');
        clearSuggestions();
    }

    return {
        suggestions,
        enrichedLabels,
        inputValue,
        setInputValue,
        onSelect,
        clearSuggestions,
        clear
    };
}
